// The whole art board: three characters and two ground materials.
//
// The style brief is the product decision here, so it is written down rather
// than discovered. This game is a parody of a completely generic fantasy MMO,
// and the art has to be generic on purpose — clean, flat, storybook-simple
// shapes that could be the placeholder art of any RPG ever shipped. Nothing
// distinctive, nothing atmospheric, nothing that looks authored.
//
// The model cannot produce a transparent background, which is why every figure
// prompt asks for a flat magenta field: the local pixel stage keys it out.
// Generation is slow, costs money and is not reproducible, so it is never part
// of a build — see scripts/assets/generate-art.ts.

export type ArtKind = "cutout" | "material";

export interface ArtJob {
  readonly id: string;
  readonly kind: ArtKind;
  /** Logical in-game size. Cutout files are stored at CUTOUT_SCALE times this. */
  readonly w: number;
  readonly h: number;
  /** Where the sprite's pivot is: on the floor under it, or in its middle. */
  readonly anchor: "feet" | "centre";
  readonly size: "1024x1024";
  readonly prompt: string;
}

/** One generated sprite as recorded in public/sprites/manifest.json. The
 *  builder, verifier, and browser all read this same contract. */
export interface SpriteManifestEntry {
  id: string;
  w: number;
  h: number;
  scale: number;
  anchor: "feet" | "centre";
  kind: ArtKind;
  file: string;
}

export interface SpriteManifest {
  sprites: SpriteManifestEntry[];
}

/** Physical raster scale kept in figure PNGs, for a soft reduced edge. */
export const CUTOUT_SCALE = 3;
/** Ground materials are reduced to this repeat size. */
export const MATERIAL_SIZE = 128;

/** The paragraph every figure brief opens with. Changing it changes the game. */
const FIGURE_STYLE = [
  "Flat 2D cartoon game sprite illustration, drawn in a clean simple storybook style with bold even outlines, large simple shapes and flat solid colour fill.",
  "Soft cel shading only: at most one slightly darker flat tone per colour. No gradients, no texture, no cross-hatching, no painterly brushwork, no gloss, no rim light, no cast shadow, no drop shadow, no glow.",
  "One single character, standing upright, centred, facing the viewer straight on, full body from head to feet, arms and legs visible, symmetrical and relaxed.",
  "Deliberately generic and plain, like default placeholder art in an ordinary fantasy role-playing game. Not stylised, not detailed, not heroic, not cute-mascot, not anime, not chibi, not pixel art, not clay, not felt, not 3D rendered, not photographic.",
  "The silhouette must still read clearly at twenty-four pixels tall, so keep the shapes few and large.",
  "The background is perfectly uniform flat #ff00ff magenta, completely empty, with no floor, no horizon, no shadow and no lighting variation. Nothing magenta anywhere on the character.",
  "No text, no letters, no numbers, no logo, no watermark, no border, no frame, no user interface, no health bar, no turnaround sheet and no multiple poses.",
].join(" ");

const MATERIAL_STYLE = [
  "Flat 2D cartoon game texture, top-down, seen straight down from directly above, filling the entire square image edge to edge.",
  "Clean simple storybook style with flat solid colour and at most one slightly darker flat tone. No gradients, no photographic texture, no noise, no painterly brushwork, no gloss, no cast shadows, no vignette and no lighting falloff.",
  "Evenly and uniformly covered across the whole square, with no focal point, no centre composition, no large empty area and no visible tile grid, square borders or seam lines.",
  "Deliberately plain and generic, like the default ground texture of an ordinary fantasy role-playing game.",
  "No characters, no creatures, no buildings, no text, no logo, no watermark, no border and no user interface.",
].join(" ");

export const ART_JOBS: readonly ArtJob[] = [
  {
    id: "player",
    kind: "cutout",
    w: 22,
    h: 30,
    anchor: "feet",
    size: "1024x1024",
    prompt: [
      FIGURE_STYLE,
      "The character is a completely generic human adventurer: an ordinary young person with plain skin, short brown hair, a plain blue belted tunic over grey trousers and simple brown boots.",
      "Empty hands, both arms hanging down at the sides. No weapon, no shield, no sword, no cape, no hat, no armour, no backpack and no jewellery.",
      "A neutral, blank, unremarkable expression. This is the most ordinary person in the world.",
    ].join(" "),
  },
  {
    id: "enemy",
    kind: "cutout",
    w: 22,
    h: 18,
    anchor: "centre",
    size: "1024x1024",
    prompt: [
      FIGURE_STYLE.replace(
        "One single character, standing upright, centred, facing the viewer straight on, full body from head to feet, arms and legs visible, symmetrical and relaxed.",
        "One single creature, centred, facing the viewer straight on, seen whole, symmetrical and squat.",
      ),
      "The creature is a simple green slime ooze: one rounded blob of green jelly, wider than it is tall, with a soft wobbly domed top and a flat wide base, and two small simple dark dot eyes near the top.",
      "One flat solid green, with one slightly darker green tone for the underside. No mouth, no teeth, no arms, no legs, no face beyond the two dots, no transparency, no bubbles, no highlights, no shine and nothing swallowed inside it.",
    ].join(" "),
  },
  {
    id: "shopkeeper",
    kind: "cutout",
    w: 24,
    h: 30,
    anchor: "feet",
    size: "1024x1024",
    prompt: [
      FIGURE_STYLE,
      "The character is a completely generic shopkeeper: a plump, friendly middle-aged human with a short grey beard, a plain red tunic, a plain brown apron and simple brown shoes.",
      "Empty hands resting in front of the waist. No weapon, no hat, no glasses, no counter, no barrel, no crate, no bottle, no coins, no goods and no shop sign.",
      "A mild, plain, welcoming expression.",
    ].join(" "),
  },
  {
    id: "terrain_grass",
    kind: "material",
    w: MATERIAL_SIZE,
    h: MATERIAL_SIZE,
    anchor: "centre",
    size: "1024x1024",
    prompt: [
      MATERIAL_STYLE,
      "The texture is plain green grassland seen from directly above: one flat medium green, evenly broken up by small simple darker green tufts and short blades scattered uniformly across the whole square.",
      "No flowers, no paths, no rocks, no dirt patches, no water and no trees.",
    ].join(" "),
  },
  {
    id: "terrain_floor",
    kind: "material",
    w: MATERIAL_SIZE,
    h: MATERIAL_SIZE,
    anchor: "centre",
    size: "1024x1024",
    prompt: [
      MATERIAL_STYLE,
      "The texture is a plain wooden plank floor seen from directly above: flat warm mid-brown boards running in one direction, evenly spaced, separated by simple thin darker brown lines.",
      "No knots, no wood grain detail, no nails, no rugs, no furniture and no wear.",
    ].join(" "),
  },
];
